import {ViewStyle, TextStyle} from 'react-native';
import {Trash2} from 'lucide-react-native';
import {useTranslation} from 'react-i18next';
import {useQueryClient} from '@tanstack/react-query';
import {ActionButton} from "@/components/common/ActionButton";
import {useCanEditMedia} from "@/data/shared-folder-hooks";
import {deleteConfidantMedia} from "@/data/shared-folder-api";
import {appDataKeys} from "@/data/app-data-keys";
import {AppDataMediaItem} from "@/data/app-data-interfaces";
import Alert from "@/lib/Alert";
import {useToast} from "@/contexts/ToastContext";

interface DeleteMediaButtonProps {
    orgId: string;
    mediaItem: AppDataMediaItem;
    variant?: 'default' | 'compact' | 'icon-only';
    size?: 'small' | 'medium' | 'large';
    style?: ViewStyle;
    textStyle?: TextStyle;
    onDeleted?: () => void;
}

export const DeleteMediaButton = ({
    mediaItem,
    size = 'medium',
    style,
    textStyle,
    onDeleted,
}: DeleteMediaButtonProps) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const canEdit = useCanEditMedia(mediaItem);
    const {showToast} = useToast();

    if (!canEdit) return null;

    const handleDelete = () => {
        Alert.alert(
            t('media.delete.confirmTitle'),
            t('media.delete.confirmMessage'),
            [
                {text: t('common.cancel'), style: 'cancel'},
                {
                    text: t('media.delete.button'),
                    style: 'destructive',
                    onPress: async () => {
                        try {
                            await deleteConfidantMedia(mediaItem.id);
                            await queryClient.invalidateQueries({queryKey: appDataKeys.all});
                            showToast(t('media.delete.success'));
                            onDeleted?.();
                        } catch (error: any) {
                            const message = error?.response?.data?.message || error?.message || '';
                            if (message.toLowerCase().includes('offer')) {
                                Alert.alert(t('media.delete.error'), t('media.delete.errorInUse'));
                            } else {
                                Alert.alert(t('media.delete.error'), t('media.delete.errorMessage'));
                            }
                        }
                    },
                },
            ]
        );
    };

    return (
        <ActionButton
            icon={Trash2}
            text={t('media.delete.button')}
            onPress={handleDelete}
            size={size}
            style={style}
            textStyle={textStyle}
        />
    );
};
